import { ReactNode, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { foldedLeftNavWidth, leftNavWidth } from '@marqvision/mds/core/LeftNavBar';
import { IcoAccountFill, IcoChat } from '@marqvision/mds/assets';
import Typography from '@marqvision/mds/core/Typography';
import { MDSButton, MDSChip, MDSMenu, MDSTag, MDSTheme } from '@marqvision/mds/core';
import { clearAuth, useAuth } from '~/apis';
import { useHasNewRequestQuery } from '~/apis/customerRequest';
import { AppContext } from '../../../AppContext';
import { RequestPanel } from './Request';

export const headerHeight = '61px';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: MDSTheme.palette.bluegray[850],
      boxShadow: 'none',
      height: headerHeight,
      width: `calc(100% - ${foldedLeftNavWidth}px)`,
      marginLeft: foldedLeftNavWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    appBarShift: {
      width: `calc(100% - ${leftNavWidth}px)`,
      marginLeft: leftNavWidth,
    },
    toolbar: {
      height: headerHeight,
      minHeight: headerHeight,
      display: 'flex',
      justifyContent: 'space-between',
      paddingLeft: '32px',
    },
    toolbarText: {
      display: 'flex',
      alignItems: 'center',
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    hide: {
      display: 'none',
    },
    toolbarButtons: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
    },
    actionButtons: {
      marginLeft: '16px',
      alignItems: 'center',
      display: 'flex',
      gap: '8px',
    },
    iconButton: {
      '& svg': {
        width: '16px',
        marginRight: '4px',
        '& path': {
          fill: 'white',
        },
      },
    },
  })
);

interface IProps {
  openNav: boolean;
  title: ReactNode;
  renderActionButton?: () => ReactNode;
  renderMenuButton?: () => ReactNode;
}

const Header = (props: IProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { openNav, title, renderActionButton, renderMenuButton } = props;

  const { clientName } = useContext(AppContext);

  const [isRequestOpen, setRequestOpen] = useState<boolean>(false);

  const { data: auth } = useAuth();

  const { data: hasNewRequest } = useHasNewRequestQuery(!!auth?.userId);

  const handleLink = (link: string) => {
    clearAuth();
    navigate({ pathname: link });
  };

  const handleRequestPanelOpen = () => {
    setRequestOpen(true);
  };

  const handlePanelClose = () => {
    setRequestOpen(false);
  };

  return (
    <>
      <AppBar
        id="header"
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: openNav,
        })}
      >
        <Toolbar className={classes.toolbar}>
          <div className={classes.toolbarText}>
            <Typography variant="T20" weight="medium" style={{ color: MDSTheme.palette.white }}>
              {title}
            </Typography>
            <div className={classes.actionButtons}>{renderActionButton && renderActionButton()}</div>
          </div>
          <div className={classes.toolbarButtons}>
            {renderMenuButton && renderMenuButton()}
            <MDSChip
              variant="fill"
              color="white"
              label="Requests"
              icon={<IcoChat />}
              endIcon={hasNewRequest?.crHasNew && <MDSTag variant="fill" color="red" size="small" label="N" />}
              onClick={handleRequestPanelOpen}
            />
            <MDSMenu
              color="transparent"
              title={
                <MDSButton variant="text" color="white" size="medium" startIcon={<IcoAccountFill />}>
                  {clientName}
                </MDSButton>
              }
              items={[{ label: 'Sign out', action: () => handleLink('/login') }]}
            />
          </div>
        </Toolbar>
      </AppBar>

      <RequestPanel isOpen={isRequestOpen} onClose={handlePanelClose} />
    </>
  );
};

export default Header;
